import {
  Lissajous1,
  Lissajous2,
  Lissajous3,
  LissajousAnnotate,
  LissajousDeploy,
  LissajousImprove,
} from "@components/Lissajous";
import { CodeShowcase } from "components/programmatic/CodeShowcase";
import { useBreakpoints } from "hooks/useBreakpoints";
import { AnnotateIcon, CheckCircleIcon, ShapeCubeIcon } from "../library/Icons";

export const OverviewSection = () => {
  const { isSmallWidth, isMediumWidth } = useBreakpoints();

  return (
    <div className="dark bg-black">
      <div className="container-xl">
        <div className="relative py-48 sm:px-24 md:px-60 md:pt-60 lg:px-80 lg:py-80 xl:px-120 xl:pt-120">
          <div className="">
            <div className="mb-[112px] hidden md:flex">
              <div className="w-1/2">
                <div className="max-w-[460px]">
                  <Header />
                  <Stats />
                </div>
              </div>
              <div className="pl-[72px]">
                <div className="mb-28">
                  <Blurb />
                </div>
                <CodeShowcase size={18} />
              </div>
            </div>
            <div className="mb-48 flex flex-col md:hidden">
              <Header />
              <div className="mb-20">
                <Blurb />
              </div>
              <div className="mb-40">
                <CodeShowcase size={14} />
              </div>
              <Stats />
            </div>

            <div className="grid grid-cols-1 gap-32 md:grid-cols-3 ">
              <IconTitleAndDescription
                icon={<LissajousAnnotate size={isMediumWidth ? 32 : 24} />}
                title="Annotate"
                description="
                Active learning, programmatic labeling, and best-in-class annotation
                 tools get you labeled data in a fraction of the time."
              />
              <IconTitleAndDescription
                icon={<LissajousDeploy size={isMediumWidth ? 32 : 24} />}
                title="Deploy"
                description="Instantly deploy state of the art NLP models that
                scale, and guarantee accuracy with human-in-the-loop workflows."
              />
              <IconTitleAndDescription
                icon={<LissajousImprove size={isMediumWidth ? 32 : 24} />}
                title="Improve"
                description="Discover issues before your users do and feel confident your model will perform with Active Testing  and our Data Debugger."
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const Header = () => (
  <h2 className="mb-16 max-w-xl font-display text-32-40 font-bold text-gray-900 dark:text-white sm:mb-24">
    An <span className="text-cyan-400">end-to-end suite of tools</span> to{" "}
    <span className="text-blue-400">train, deploy, and improve</span> NLP models
    fast.
  </h2>
);

const Blurb = () => (
  <div className="prose max-w-md text-14-22 text-white md:text-16-24 md:leading-[28px]">
    {/* Humanloop takes the pain out of building and maintaining NLP. We make it
    easy to get annotated data, scale deployments and improve datasets, so you
    can focus on building next generation apps and services.  */}
    Humanloop makes it easy for you to get annotated data, build NLP and scale
    deployments, so you can deliver on the promise of AI.
    <br />
    <br />
    Spin up a project today.
  </div>
);

const Stats = () => (
  <div className="grid grid-cols-2 gap-x-32 gap-y-20 md:grid-cols-3 ">
    <ShowcaseStatistic title="Label data" value="10x faster" />
    <ShowcaseStatistic title="Prototype ideas" value="10x quicker" />
    <ShowcaseStatistic title="Deploy and scale" value="10x easier" />
    <div className="block md:hidden">
      <ShowcaseStatistic title="Quick start in" value="30 minutes" />
    </div>
  </div>
);

const IconTitleAndDescription = ({
  icon,
  title,
  description,
}: {
  icon: React.ReactNode;
  title: string;
  description: string;
}) => {
  const { isMediumWidth } = useBreakpoints();

  return (
    <div className="flex flex-col gap-12">
      <div className="flex flex-row gap-10 text-white md:flex-col md:gap-12">
        {icon}
        <div className="flex-1 text-16-24 font-bold text-white md:text-18-24">
          {title}
        </div>
      </div>

      <div className="max-w-md text-14-22 text-gray-500 md:text-14-24">
        {description}
      </div>
    </div>
  );
};

export const ShowcaseStatistic = ({
  title,
  value,
}: {
  title: string;
  value: string;
}) => {
  return (
    <div className="flex flex-col gap-2 md:gap-8">
      <h5 className="text-12-14 font-bold text-gray-600 md:text-13-16">
        {title}
      </h5>
      <p className="text-18-24 font-bold text-white  md:text-20-24 ">{value}</p>
    </div>
  );
};
